import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "hasScrolling", "headerComponent", "mainBackgroundColor", "mainSectionFlush", "scrollDirection", "scrollElementRef", "sidebarBackgroundColor", "sidebarComponent", "tabs", "title", "use"];
import styled, { useTheme } from 'foundations-theming/utils';
import { AbstractPageTemplate } from '../AbstractPageTemplate/AbstractPageTemplate';
import { Main } from '../Main/Main';
import { ScrollingColumn } from '../ScrollingColumn/ScrollingColumn';
import * as CustomRenderer from '../AbstractPageTemplate/customRenderer';
import { ShareScrollElement } from '../ScrollingColumn/ShareScrollElement';
import { useEffect } from 'react';
import { MERCURY_LAYER } from '../../constants';
import { EDITOR_MODE_CLASSNAME } from '../../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MainInnerWrapper = styled.div.withConfig({
  displayName: "EditorPage__MainInnerWrapper"
})(({
  $mainSectionFlush,
  $hasScrolling,
  theme
}) => {
  return {
    height: '100%',
    padding: !$mainSectionFlush && $hasScrolling ? theme.space['500'] : undefined
  };
});
const SidebarWrapper = styled.div.withConfig({
  displayName: "EditorPage__SidebarWrapper"
})(["flex-shrink:0;box-shadow:1px 0 3px 0 rgba(0,0,0,0.25);background-color:", ";z-index:", ";height:100%;"], props => props.$backgroundColor, MERCURY_LAYER);
const MainOuterWrapper = styled.div.withConfig({
  displayName: "EditorPage__MainOuterWrapper"
})(({
  $backgroundColor,
  $computedFlush,
  theme
}) => {
  return Object.assign({
    backgroundColor: $backgroundColor,
    flex: 1,
    padding: theme.space['500'],
    position: 'relative'
  }, !!$computedFlush && {
    padding: theme.space['0'],
    width: '100%',
    height: '100%'
  });
});
const StaticSidebar = styled.div.withConfig({
  displayName: "EditorPage__StaticSidebar"
})(["height:100%;"]);
function renderEditorSidebar(sidebarComponent, sidebarBackgroundColor, hasScrolling) {
  if (!sidebarComponent) return null;
  const RenderedSidebar = hasScrolling ? ScrollingColumn : StaticSidebar;
  const renderedSidebarProps = hasScrolling ? {
    blockSize: '100%'
  } : null;
  return /*#__PURE__*/_jsx(SidebarWrapper, {
    $backgroundColor: sidebarBackgroundColor,
    children: /*#__PURE__*/_jsx(RenderedSidebar, Object.assign({}, renderedSidebarProps, {
      children: CustomRenderer.render(sidebarComponent)
    }))
  });
}
const EditorInner = styled.div.withConfig({
  displayName: "EditorPage__EditorInner"
})(["display:flex;align-items:stretch;height:100%;position:absolute;width:100%;left:0;top:0;"]);
const MainSection = ({
  children,
  hasScrolling = true,
  mainSectionBackgroundColor,
  scrollDirection,
  scrollElementRef,
  sidebarBackgroundColor,
  sidebarComponent,
  mainSectionFlush = false
}) => {
  const RenderedMain = hasScrolling ? ScrollingColumn : Main;
  const theme = useTheme();
  sidebarBackgroundColor = sidebarBackgroundColor || theme.color['fill-surface-raised'];
  const computedFlush = hasScrolling ? true : mainSectionFlush;
  const scrollableProps = hasScrolling ? {
    role: 'main',
    scrollDirection,
    blockSize: '100%',
    scrollElementRef
  } : null;
  return /*#__PURE__*/_jsxs(EditorInner, {
    children: [renderEditorSidebar(sidebarComponent, sidebarBackgroundColor, hasScrolling), /*#__PURE__*/_jsx(MainOuterWrapper, {
      $backgroundColor: mainSectionBackgroundColor,
      $computedFlush: computedFlush,
      children: /*#__PURE__*/_jsx(RenderedMain, Object.assign({
        flush: true,
        blockSize: computedFlush ? '100%' : `calc(100% - ${theme.space['1000']})`,
        inlineSize: computedFlush ? '100%' : `calc(100% - ${theme.space['1000']})`,
        paddingBlockEnd: "0",
        position: "absolute"
      }, scrollableProps, {
        children: /*#__PURE__*/_jsx(MainInnerWrapper, {
          $hasScrolling: hasScrolling,
          $mainSectionFlush: mainSectionFlush,
          children: children
        })
      }))
    })]
  });
};
const InternalEditorPage = _ref => {
  let {
      children,
      hasScrolling,
      headerComponent,
      mainBackgroundColor,
      mainSectionFlush,
      scrollDirection,
      scrollElementRef,
      sidebarBackgroundColor,
      sidebarComponent,
      tabs,
      title,
      use
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const mainPanelBackgroundColor = use === 'dark' ? theme.color['fill-surface-raised'] : mainBackgroundColor;

  /**
   * Suppress the navigation
   * @see https://github.com/HubSpotEngineering/UIComponents/blob/67b245bc863bb51da1ea4e57bc428a8a75939fb1/HubStyle/static/stylesheets/hubstyle/_templates.sass#L23-L28
   */
  useEffect(() => {
    const attr = 'data-editor-page-count';
    const getCount = () => parseInt(document.body.getAttribute(attr) || '0', 10);
    document.body.setAttribute(attr, (getCount() + 1).toString());
    document.body.classList.add(EDITOR_MODE_CLASSNAME);
    return () => {
      const count = Math.max(0, getCount() - 1);
      document.body.setAttribute(attr, count.toString());
      if (count === 0) {
        document.body.classList.remove(EDITOR_MODE_CLASSNAME);
        document.body.removeAttribute(attr);
      }
    };
  }, []);
  return /*#__PURE__*/_jsx(AbstractPageTemplate, Object.assign({}, rest, {
    contentAreaFlush: true,
    headerComponent: headerComponent,
    horizontalDivider: true,
    isEditor: true,
    blockSize: "100vh",
    mainSectionBackgroundColor: mainPanelBackgroundColor,
    MainSection: MainSection,
    mainSectionProps: {
      mainSectionFlush,
      hasScrolling,
      scrollDirection,
      scrollElementRef,
      sidebarBackgroundColor
    },
    pageLayout: "full-width",
    sidebarComponent: sidebarComponent,
    tabs: tabs,
    title: title,
    children: children
  }));
};
export const NoShareScrollEditorPage = InternalEditorPage;
export const EditorPage = ShareScrollElement(InternalEditorPage);
EditorPage.displayName = 'EditorPage';